/**
 * @file interpret/components/callSettings.ts
 * @brief Call mode's settings, kept on this computer (CallModeControls.tsx).
 *        Out of the component file so fast refresh keeps working.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useCallback, useState } from "react";

export type CallSettings = {
  on: boolean;
  sendDeviceId: string;
  /** The send device's name: "Verblizr Microphone" also becomes the Mac's mic. */
  sendLabel: string;
  translationOnly: boolean;
  /** Where you hear their translation; "" = headphones when connected. */
  hearDeviceId: string;
  /** Hear your own translation too (DES 03-10-2026: on by default). */
  hearOwn: boolean;
  /** The other side hears their own translation in the call too (on by default). */
  theyHearOwn: boolean;
  /** A beep into the call after your translation, "your turn" (on by default). */
  turnBeep: boolean;
};

const STORE_KEY = "vz.interpret.callMode";
const OFF: CallSettings = {
  on: false,
  sendDeviceId: "",
  sendLabel: "",
  translationOnly: false,
  hearDeviceId: "",
  hearOwn: true,
  theyHearOwn: true,
  turnBeep: true,
};

function readStored(): CallSettings {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return OFF;
    const v = JSON.parse(raw) as Partial<CallSettings>;
    return {
      on: v.on === true,
      sendDeviceId: typeof v.sendDeviceId === "string" ? v.sendDeviceId : "",
      sendLabel: typeof v.sendLabel === "string" ? v.sendLabel : "",
      translationOnly: v.translationOnly === true,
      hearDeviceId: typeof v.hearDeviceId === "string" ? v.hearDeviceId : "",
      hearOwn: v.hearOwn !== false,
      theyHearOwn: v.theyHearOwn !== false,
      turnBeep: v.turnBeep !== false,
    };
  } catch {
    return OFF;
  }
}

/** Call mode's settings, kept on this computer. */
export function useCallSettings(): [CallSettings, (next: CallSettings) => void] {
  const [value, setValue] = useState<CallSettings>(readStored);
  const set = useCallback((next: CallSettings) => {
    setValue(next);
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* private window: kept for this visit only */
    }
  }, []);
  return [value, set];
}
